/**
 * LimitsDistributionPanel -- the what-if preview inside the Member
 * Liquidity policy editor (overdraft-coverage arc OC-3b, UXD-15: what-if
 * before publish).
 *
 * Both preview modes call POST /api/v1/tempo/policy/what-if and render
 * the server-computed LimitsDistribution -- the SAME pure scorecard that
 * produces production recommendations evaluates the preview server-side;
 * this component contains no scorecard math:
 *
 *   - "Preview these dials": the CURRENT form state as candidate
 *     parameters (mapped by the page's existing buildInput), before any
 *     publish.
 *   - "Preview effective policy": no candidate -- the currently effective
 *     version evaluates.
 *
 * The 404 family renders named empty states (no policy version on the
 * effective path; no scored accounts when the nightly batch has not run),
 * and a 400 semantic rejection renders its reason-coded details like the
 * publish Alert does.
 *
 * A candidate preview describes the dials it was run with. Once any dial
 * changes, its distribution, rejection or pending evaluation no longer
 * describes the form on screen, so the panel shows nothing until the next
 * preview (GH #2747, operator ruling 2026-10-21). An effective-policy preview
 * does not read the dials and stays.
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  BodyText,
  Button,
  Card,
  DataGrid,
  Heading,
  MetricCard,
  Skeleton,
  Badge,
  type BadgeProps,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import {
  useLimitsWhatIf,
  type LimitsDistribution,
  type LimitsDistributionBucket,
  type LiquidityPolicyParameters,
} from '../../hooks/useTempoPolicy';
import { PREVIEW_COPY } from './coverage-policy/labels';

const TIER_DISPLAY: readonly {
  tier: string;
  label: string;
  variant: NonNullable<BadgeProps['variant']>;
}[] = [
  { tier: 'NONE', label: 'No coverage', variant: 'neutral' },
  { tier: 'ENTRY', label: 'Entry', variant: 'success' },
  { tier: 'STANDARD', label: 'Standard', variant: 'success' },
  { tier: 'GRADUATED_LINE_OFFER', label: 'Graduated offer', variant: 'warning' },
];

function usd(value: number): string {
  return `$${value.toLocaleString('en-US')}`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Display label for a bucket. The zero bucket is the exact no-coverage
 * band; positive bands are [rangeLowUsd, rangeHighUsd) with the scorecard
 * flooring limits to $25 increments, so the highest representable value
 * in a band is rangeHighUsd - 25.
 */
function bucketRangeLabel(bucket: LimitsDistributionBucket): string {
  if (bucket.rangeLowUsd === 0 && bucket.rangeHighUsd === 0) {
    return '$0 (no coverage)';
  }
  return `${usd(bucket.rangeLowUsd)} - ${usd(bucket.rangeHighUsd - 25)}`;
}

export function LimitsDistributionPanel({
  getCandidateParameters,
  dialsKey,
}: {
  /** Current form dials as spec parameters; null when the form is invalid (the page surfaces the field error). */
  getCandidateParameters: () => LiquidityPolicyParameters | null;
  /** Changes with every dial edit; a candidate preview run under another key is stale. */
  dialsKey: string;
}) {
  const whatIf = useLimitsWhatIf();
  const [mode, setMode] = useState<'candidate' | 'effective' | null>(null);
  const [previewedDials, setPreviewedDials] = useState<string | null>(null);

  // The clear is one-way: once the dials move, editing them back to the
  // previewed values does not bring the earlier result back. null matches no
  // key, so nothing renders until previewCandidate records a new one.
  if (mode === 'candidate' && previewedDials !== null && previewedDials !== dialsKey) {
    setPreviewedDials(null);
  }
  const candidateStale = mode === 'candidate' && previewedDials !== dialsKey;
  const isPending = whatIf.isPending && !candidateStale;
  const distribution: LimitsDistribution | undefined = candidateStale ? undefined : whatIf.data;

  const bucketColumns = useMemo<ColumnDef<LimitsDistributionBucket, unknown>[]>(
    () => [
      {
        id: 'range',
        header: 'Limit range',
        cell: ({ row }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {bucketRangeLabel(row.original)}
          </span>
        ),
        size: 200,
      },
      {
        accessorKey: 'accountCount',
        header: 'Accounts',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {(getValue() as number).toLocaleString('en-US')}
          </span>
        ),
        size: 120,
      },
      {
        id: 'share',
        header: 'Share',
        cell: ({ row }) => {
          const total = distribution?.accountsEvaluated ?? 0;
          const share = total > 0 ? (row.original.accountCount / total) * 100 : 0;
          return (
            <span className="text-sm font-mono text-secondary tabular-nums leading-normal">
              {share.toFixed(1)}%
            </span>
          );
        },
        size: 100,
      },
    ],
    [distribution?.accountsEvaluated],
  );

  const reasonRows = useMemo(
    () =>
      Object.entries(distribution?.reasonCodeCounts ?? {})
        .map(([code, count]) => ({ code, count }))
        .sort((a, b) => b.count - a.count),
    [distribution?.reasonCodeCounts],
  );

  const reasonColumns = useMemo<ColumnDef<{ code: string; count: number }, unknown>[]>(
    () => [
      {
        accessorKey: 'code',
        header: 'Reason code',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary leading-normal">{getValue() as string}</span>
        ),
        size: 280,
      },
      {
        accessorKey: 'count',
        header: 'Accounts',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {(getValue() as number).toLocaleString('en-US')}
          </span>
        ),
        size: 120,
      },
    ],
    [],
  );

  function previewCandidate() {
    const parameters = getCandidateParameters();
    if (!parameters) return;
    setMode('candidate');
    setPreviewedDials(dialsKey);
    whatIf.mutate(parameters);
  }

  function previewEffective() {
    setMode('effective');
    whatIf.mutate(null);
  }

  const error = candidateStale ? null : whatIf.error;
  const emptyState404 =
    error && error.status === 404
      ? error.code === 'POLICY_NOT_FOUND'
        ? 'No policy version has been published yet. Preview these dials instead, or publish a first version.'
        : 'No scored accounts yet -- the nightly batch has not run for your institution.'
      : null;

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <Heading level={2} size="card" className="font-sans mb-1">
            Limits Distribution Preview
          </Heading>
          <BodyText variant="ui" className="text-sm text-tertiary">
            Evaluates dials against your institution&apos;s scored accounts server-side -- the same
            scorecard that produces production recommendations. Nothing is persisted.
          </BodyText>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Button variant="secondary" onClick={previewEffective} disabled={isPending}>
            Preview effective policy
          </Button>
          <Button variant="secondary" onClick={previewCandidate} disabled={isPending}>
            {isPending ? 'Evaluating...' : 'Preview these dials'}
          </Button>
        </div>
      </div>

      {isPending && (
        <div className="space-y-3 mt-4">
          <Skeleton className="h-6 w-72" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}

      {!isPending && emptyState404 && (
        <div className="p-4 mt-4 rounded-sm bg-accent/5 border border-recessed">
          <BodyText variant="ui" className="text-sm text-secondary">
            {emptyState404}
          </BodyText>
        </div>
      )}

      {!isPending && error && !emptyState404 && (
        <Alert
          type={error.status === 400 ? 'warning' : 'error'}
          title={error.status === 400 ? 'Preview rejected' : 'Preview failed'}
        >
          <div className="space-y-1">
            <div>{error.message}</div>
            {error.details && error.details.length > 0 && (
              <ul className="list-disc pl-5">
                {error.details.map((detail, index) => (
                  <li key={index}>
                    {detail.issue ?? detail.message ?? 'Validation failure'}
                    {detail.path ? ` (${detail.path})` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Alert>
      )}

      {!isPending && distribution && (
        <div className="space-y-6 mt-4">
          <BodyText variant="ui" className="text-sm text-tertiary">
            {mode === 'effective' ? 'Effective policy' : 'Candidate dials'} evaluated over{' '}
            {distribution.accountsEvaluated.toLocaleString('en-US')} scored accounts (latest score date{' '}
            {distribution.scoreDate}; computed {formatDateTime(distribution.asOf)}).
          </BodyText>

          {/* The preview's basis (GH #2931 deliverable 1). The service scores
              each account's latest snapshot against its CURRENT Reg E opt-in
              and cool-off state, while a persisted recommendation carries the
              state of the run that wrote it, so the two can disagree after an
              opt-in backfill. One string, shared with the Coverage Policy
              redesign modules. */}
          <BodyText variant="ui" className="text-sm text-tertiary">
            {PREVIEW_COPY.latestSnapshotLine}
          </BodyText>

          {/* The cure-cap basis the evaluated parameters resolved to, rendered
              as the spec's own enum value. Two distributions over one book are
              comparable only against it: a candidate run and the effective
              policy can resolve to different bases, and the tier counts move
              accordingly. The line is omitted when the field is absent, which
              the spec defines as a short-circuited distribution where no basis
              was resolved -- an absent basis is not an ALL_DAYS basis, and
              labeling it as one would state a resolution that did not happen. */}
          {distribution.cureCapBasis && (
            <BodyText variant="ui" className="text-sm text-tertiary">
              Cure-cap basis{' '}
              <span className="font-mono text-primary">{distribution.cureCapBasis}</span>.
            </BodyText>
          )}

          <div className="flex flex-wrap items-center gap-6">
            {TIER_DISPLAY.map(({ tier, label, variant }) => (
              <div key={tier} className="flex items-center gap-2">
                <Badge variant={variant} size="small">
                  {label}
                </Badge>
                <span className="text-sm font-mono text-primary tabular-nums">
                  {(distribution.tierCounts[tier] ?? 0).toLocaleString('en-US')}
                </span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard label="Median limit" value={usd(distribution.medianLimitUsd)} />
            <MetricCard label="Total recommended" value={usd(distribution.totalRecommendedLimitUsd)} />
            <MetricCard
              label="Accounts evaluated"
              value={distribution.accountsEvaluated.toLocaleString('en-US')}
            />
          </div>

          <div>
            <Heading level={3} size="label" className="font-sans mb-2">
              Limit buckets
            </Heading>
            <DataGrid<LimitsDistributionBucket>
              data={distribution.buckets}
              columns={bucketColumns}
              getRowId={(row) => `${row.rangeLowUsd}-${row.rangeHighUsd}`}
              className="w-full rounded-b-sm shadow-sm"
            />
          </div>

          <div>
            <Heading level={3} size="label" className="font-sans mb-2">
              Reason codes
            </Heading>
            <DataGrid<{ code: string; count: number }>
              data={reasonRows}
              columns={reasonColumns}
              getRowId={(row) => row.code}
              className="w-full rounded-b-sm shadow-sm"
            />
          </div>
        </div>
      )}
    </Card>
  );
}
